import { GlForm, GlButton, GlSprintf } from '@gitlab/ui';
import { mapActions } from 'pinia';
import { createAlert } from '~/alert';
import csrf from '~/lib/utils/csrf';
import { STATUS_MERGED } from '~/issues/constants';
import { BV_HIDE_MODAL, BV_SHOW_MODAL } from '~/lib/utils/constants';
import { HTTP_STATUS_UNAUTHORIZED } from '~/lib/utils/http_status';
import { s__, __, n__, sprintf } from '~/locale';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { useBatchComments } from '~/batch_comments/store';
import approvalsMixin from '../../mixins/approvals';
import eventHub from '../../event_hub';
import StateContainer from '../state_container.vue';
import { INVALID_RULES_DOCS_PATH } from '../../constants';
import ApprovalsSummary from './approvals_summary.vue';
import { FETCH_LOADING, APPROVE_ERROR, UNAPPROVE_ERROR } from './messages';
const SUBSCRIPTION_FALLBACK_TIMEOUT = 5000;
export default {
    name: 'MRWidgetApprovals',
    components: {
        ApprovalsSummary,
        StateContainer,
        GlButton,
        GlSprintf,
        GlForm
    },
    csrf,
    mixins: [
        approvalsMixin
    ],
    props: {
        mr: {
            type: Object,
            required: true
        },
        service: {
            type: Object,
            required: true
        },
        isOptionalDefault: {
            type: Boolean,
            required: false,
            default: null
        },
        modalId: {
            type: String,
            required: false,
            default: null
        },
        requirePasswordToApprove: {
            type: Boolean,
            required: false,
            default: false
        },
        collapsed: {
            type: Boolean,
            required: false,
            default: false
        },
        hasAllApprovals: {
            type: Boolean,
            required: false,
            default: false
        },
        actionButtons: {
            type: Array,
            required: false,
            default: ()=>[]
        }
    },
    emits: [
        'toggle',
        'clearError'
    ],
    data () {
        return {
            hasApprovalAuthError: false,
            isApproving: false
        };
    },
    computed: {
        isLoading () {
            return this.$apollo.queries.approvals.loading || !this.approvals;
        },
        isApproved () {
            return this.hasAllApprovals;
        },
        isOptional () {
            return this.isOptionalDefault !== null ? this.isOptionalDefault : !this.approvedBy.length;
        },
        hasAction () {
            return Boolean(this.action);
        },
        invalidRules () {
            return this.approvals.approvalState?.rules?.filter((rule)=>rule.invalid) || [];
        },
        invalidApprovedRules () {
            return this.invalidRules.filter((rule)=>rule.allowMergeWhenInvalid);
        },
        invalidFailedRules () {
            return this.invalidRules.filter((rule)=>!rule.allowMergeWhenInvalid);
        },
        hasInvalidRules () {
            return this.mr.mergeRequestApproversAvailable && this.invalidRules.length;
        },
        hasInvalidApprovedRules () {
            return this.mr.mergeRequestApproversAvailable && this.invalidApprovedRules.length;
        },
        hasInvalidFailedRules () {
            return this.mr.mergeRequestApproversAvailable && this.invalidFailedRules.length;
        },
        approvedBy () {
            return this.approvals?.approvedBy?.nodes || [];
        },
        userHasApproved () {
            return this.approvedBy.some((approver)=>getIdFromGraphQLId(approver.id) === gon.current_user_id);
        },
        userCanApprove () {
            return Boolean(this.approvals.userPermissions.canApprove);
        },
        showApprove () {
            return !this.userHasApproved && this.userCanApprove && this.mr.isOpen;
        },
        showUnapprove () {
            return this.userHasApproved && !this.userCanApprove && this.mr.state !== STATUS_MERGED;
        },
        showApproveButton () {
            return (!this.requireSamlAuthToApprove || this.showUnapprove) && this.action;
        },
        approvalText () {
            // Repeating a text of this to keep i18n easier to do (vs, construcing a compound string)
            if (this.requireSamlAuthToApprove) {
                return this.isApproved && this.approvedBy.length > 0 ? s__('mrWidget|Approve additionally with SAML') : s__('mrWidget|Approve with SAML');
            }
            return this.isApproved && this.approvedBy.length > 0 ? s__('mrWidget|Approve additionally') : s__('mrWidget|Approve');
        },
        action () {
            // Use the default approve action, only if we aren't using the auth component for it
            if (this.showApprove) {
                return {
                    text: this.approvalText,
                    category: this.isApproved ? 'secondary' : 'primary',
                    variant: 'confirm',
                    action: ()=>this.approve()
                };
            }
            if (this.showUnapprove) {
                return {
                    text: s__('mrWidget|Revoke approval'),
                    variant: 'default',
                    action: ()=>this.unapprove()
                };
            }
            return null;
        },
        pluralizedFailedRuleText () {
            return sprintf(n__("mrWidget|%{dangerStart}1 rule can't be approved%{dangerEnd}", "mrWidget|%{dangerStart}%{count} rules can't be approved%{dangerEnd}", this.invalidFailedRules.length), {
                count: this.invalidFailedRules.length
            });
        },
        pluralizedApprovedRuleText () {
            return sprintf(n__('mrWidget|1 invalid rule has been approved automatically', 'mrWidget|%{count} invalid rules have been approved automatically', this.invalidApprovedRules.length), {
                count: this.invalidApprovedRules.length
            });
        },
        pluralizedRuleText () {
            return [
                this.hasInvalidFailedRules ? this.pluralizedFailedRuleText : null,
                this.hasInvalidApprovedRules ? this.pluralizedApprovedRuleText : null
            ].filter((text)=>Boolean(text)).join(', ').concat('.');
        },
        samlApprovalPath () {
            return this.mr.samlApprovalPath;
        },
        requireSamlAuthToApprove () {
            return this.mr.requireSamlAuthToApprove;
        }
    },
    watch: {
        userHasApproved () {
            this.stopApproving();
        }
    },
    beforeDestroy () {
        clearTimeout(this.subscriptionFallbackTimeout);
    },
    methods: {
        ...mapActions(useBatchComments, [
            'clearDrafts'
        ]),
        approve () {
            if (this.requirePasswordToApprove) {
                this.$root.$emit(BV_SHOW_MODAL, this.modalId);
                return;
            }
            const { hasDrafts } = useBatchComments();
            this.updateApproval(()=>this.service.approveMergeRequest({
                    publish_review: hasDrafts
                }).then(()=>this.clearDrafts()), ()=>this.alerts.push(createAlert({
                    message: APPROVE_ERROR
                })));
        },
        approveWithAuth (data) {
            const { hasDrafts } = useBatchComments();
            this.updateApproval(()=>this.service.approveMergeRequestWithAuth({
                    approval_password: data,
                    publish_review: hasDrafts
                }).then(()=>{
                    this.$root.$emit(BV_HIDE_MODAL, this.modalId);
                    return this.clearDrafts();
                }), (error)=>{
                if (error?.response?.status === HTTP_STATUS_UNAUTHORIZED) {
                    this.hasApprovalAuthError = true;
                    return;
                }
                this.alerts.push(createAlert({
                    message: APPROVE_ERROR
                }));
            });
        },
        unapprove () {
            this.updateApproval(()=>this.service.unapproveMergeRequest(), ()=>this.alerts.push(createAlert({
                    message: UNAPPROVE_ERROR
                })));
        },
        updateApproval (serviceFn, errFn) {
            this.isApproving = true;
            this.clearError();
            return serviceFn().then(()=>{
                // Approval changes which auto-merge strategies are available; refresh them so the
                // merge button doesn't stay stale until the next (backed-off) poll (#629566).
                eventHub.$emit('mr-widget-check-status');
                if (this.isApproving) this.startSubscriptionFallback();
            }).catch((error)=>{
                this.stopApproving();
                errFn(error);
            });
        },
        startSubscriptionFallback () {
            this.subscriptionFallbackTimeout = setTimeout(()=>{
                this.$apollo.queries.approvals.refetch().catch(()=>{}).finally(()=>this.stopApproving());
            }, SUBSCRIPTION_FALLBACK_TIMEOUT);
        },
        stopApproving () {
            clearTimeout(this.subscriptionFallbackTimeout);
            this.isApproving = false;
        }
    },
    FETCH_LOADING,
    linkToInvalidRules: INVALID_RULES_DOCS_PATH,
    i18n: {
        learnMore: __('Learn more.')
    }
};
